import { Box, Stack, styled, Tooltip, TooltipProps } from "@mui/material";
import React, { memo } from "react";

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor:
      theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.common.white,
    color: theme.palette.mode === "dark" ? theme.palette.common.white : theme.palette.text.primary,
    padding: 0,
    borderRadius: 24,
    boxShadow:
      theme.palette.mode === "dark"
        ? "0px 2px 8px rgba(0, 0, 0, 0.3)"
        : "0px 2px 8px rgba(0, 0, 0, 0.1)",
    maxWidth: "none",
    overflow: "hidden",
    margin: "0 !important",
    transform: "translateY(-8px)",
  },
  [`& .MuiTooltip-arrow`]: {
    color: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.common.white,
  },
}));

const TooltipContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.75, 2),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 500,
  fontSize: 12,
  minWidth: 100,
  height: 28,
  whiteSpace: "nowrap",
  lineHeight: 1,
}));

interface WarcTooltipProps extends Omit<TooltipProps, "title"> {
  children: React.ReactElement;
  message: string;
  showWarcLabel?: boolean;
  /**
   * If true, the tooltip will not be rendered at all.
   * Note: This is different from disabling the wrapped component.
   */
  disabled?: boolean;
}

const WarcTooltipComponent: React.FC<WarcTooltipProps> = ({
  children,
  message,
  showWarcLabel = true,
  disabled = false,
  ...props
}) => {
  // If the tooltip itself is disabled, return just the children
  if (disabled) {
    return children;
  }

  const tooltipContent = (
    <TooltipContent>
      {showWarcLabel && ""}
      {message}
    </TooltipContent>
  );

  return (
    <StyledTooltip
      title={tooltipContent}
      arrow
      placement="top"
      enterDelay={100}
      leaveDelay={100}
      {...props}
    >
      {/* Ensure the child can receive pointer events even if disabled */}
      <Stack
        component="span"
        direction="row"
        alignItems="center"
        sx={{
          height: "100%",
          width: "auto",
        }}
      >
        {children}
      </Stack>
    </StyledTooltip>
  );
};

export const WarcTooltip = memo(WarcTooltipComponent);
